import { UserPlus, MessageSquare, BarChart3, Trophy, ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const steps = [
  {
    number: "01",
    icon: UserPlus,
    title: "Create Your Account",
    description: "Sign up in seconds and access your personalized dashboard.",
    color: "primary",
  },
  {
    number: "02",
    icon: MessageSquare,
    title: "Start a Role-Play",
    description: "Choose a scenario and engage with our AI buyer through voice or text. Practice role-plays, objection drills, or speed-sells.",
    color: "accent",
  },
  {
    number: "03",
    icon: BarChart3,
    title: "Get Instant Feedback",
    description: "Receive detailed scoring across multiple sales performance categories, voice and visual analytics with actionable coaching tips.",
    color: "purple",
  },
  {
    number: "04",
    icon: Trophy,
    title: "Track & Improve",
    description: "Monitor your progress over time, identify weak areas, and watch your scores climb as you practice.",
    color: "emerald",
  },
];

const StepCard = ({ step, index, totalSteps }: { step: typeof steps[0]; index: number; totalSteps: number }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  
  const colorClasses = {
    primary: {
      icon: "text-primary",
      iconBg: "bg-primary/10 group-hover:bg-primary/20",
      number: "from-primary to-primary/50",
      glow: "group-hover:shadow-[0_0_40px_-5px_hsl(199_89%_48%/0.3)]",
      border: "group-hover:border-primary/30",
      arrow: "text-primary/50",
    },
    accent: {
      icon: "text-accent",
      iconBg: "bg-accent/10 group-hover:bg-accent/20",
      number: "from-accent to-accent/50",
      glow: "group-hover:shadow-[0_0_40px_-5px_hsl(185_85%_50%/0.3)]",
      border: "group-hover:border-accent/30",
      arrow: "text-accent/50",
    },
    purple: {
      icon: "text-electric-purple",
      iconBg: "bg-electric-purple/10 group-hover:bg-electric-purple/20",
      number: "from-electric-purple to-electric-purple/50",
      glow: "group-hover:shadow-[0_0_40px_-5px_hsl(260_70%_60%/0.3)]",
      border: "group-hover:border-electric-purple/30",
      arrow: "text-electric-purple/50",
    },
    emerald: {
      icon: "text-emerald-400",
      iconBg: "bg-emerald-400/10 group-hover:bg-emerald-400/20",
      number: "from-emerald-400 to-emerald-400/50",
      glow: "group-hover:shadow-[0_0_40px_-5px_hsl(160_84%_39%/0.3)]",
      border: "group-hover:border-emerald-400/30",
      arrow: "text-emerald-400/50",
    },
  };

  const colors = colorClasses[step.color as keyof typeof colorClasses];
  const Icon = step.icon;

  return (
    <div 
      ref={ref}
      className={`relative group transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 0.15}s` }}
    >
      {/* Arrow Connector (desktop only) */}
      {index < totalSteps - 1 && (
        <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
          <ArrowRight className={`w-6 h-6 ${colors.arrow}`} />
        </div>
      )}
      
      <div className={`glass rounded-2xl p-6 sm:p-8 h-full border border-white/5 transition-all duration-500 ${colors.glow} ${colors.border}`}>
        {/* Top Row: Number + Icon */}
        <div className="flex items-center justify-between mb-6">
          {/* Gradient Number */}
          <span className={`font-display text-4xl sm:text-5xl font-bold bg-gradient-to-b ${colors.number} bg-clip-text text-transparent`}>
            {step.number}
          </span>
          
          {/* Icon with glow effect */}
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${colors.iconBg} ${colors.icon}`}>
            <Icon className="w-7 h-7" />
          </div>
        </div>
        
        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-white/10 via-white/5 to-transparent mb-5" />
        
        {/* Content */}
        <h3 className="font-display font-semibold text-xl mb-3 text-foreground">
          {step.title}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {step.description}
        </p>
      </div>
    </div>
  );
};

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 sm:py-32 relative">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full glass text-sm font-medium text-primary mb-4 border border-primary/20">
            Simple Process
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            How It <span className="text-gradient bg-gradient-to-r from-primary via-accent to-electric-purple bg-clip-text text-transparent">Works</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            From signup to sales mastery in four simple steps
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {steps.map((step, index) => (
            <StepCard key={step.number} step={step} index={index} totalSteps={steps.length} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
